import React, { useState, useRef, useEffect } from 'react';
import AbcNotation from './AbcNotation';
import ActionConfirmModal from './ActionConfirmModal';

export default function FullscreenViewer({
  type,
  tune,
  tunes,
  currentIndex,
  contextLabel = '',
  contextName = '',
  onClose,
  onNavigate,
  onLyricsChange,
  onNotesChange
}) {
  const fontSizeStorageKey = `fullscreen-viewer-font-size:${type || 'lyrics'}`;
  const abcScaleStorageKey = `fullscreen-viewer-abc-scale:${type || 'abc'}`;
  const [fontSize, setFontSize] = useState(() => {
    if (typeof window === 'undefined') return 1.2;
    const saved = Number.parseFloat(localStorage.getItem(fontSizeStorageKey) || '');
    return Number.isFinite(saved) ? Math.min(2.5, Math.max(0.8, saved)) : 1.2;
  });
  const [abcScale, setAbcScale] = useState(() => {
    if (typeof window === 'undefined') return 1.0;
    const saved = Number.parseFloat(localStorage.getItem(abcScaleStorageKey) || '');
    if (Number.isFinite(saved)) return Math.min(2.0, Math.max(0.4, saved));
    return window.innerWidth <= 640 ? 1.15 : 1.0;
  });
  const isTextType = type === 'lyrics' || type === 'notes';
  const currentTextValue = type === 'notes' ? (tune.notes || '') : (tune.lyrics || '');
  const [editedText, setEditedText] = useState(currentTextValue);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const overlayRef = useRef(null);
  const textareaRef = useRef(null);
  const wheelAccum = useRef(0);
  const wheelTimer = useRef(null);
  const wheelLocked = useRef(false);
  const isDirty = isTextType && editedText !== currentTextValue;
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(fontSizeStorageKey, String(fontSize));
  }, [fontSize, fontSizeStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(abcScaleStorageKey, String(abcScale));
  }, [abcScale, abcScaleStorageKey]);

  // Sync text editor value when tune/type changes (e.g. navigating between tunes)
  useEffect(() => {
    setEditedText(type === 'notes' ? (tune.notes || '') : (tune.lyrics || ''));
  }, [type, tune.id, tune.lyrics, tune.notes]);

  const handleSaveText = () => {
    if (!isDirty) return;
    if (type === 'notes' && onNotesChange) onNotesChange(editedText);
    if (type === 'lyrics' && onLyricsChange) onLyricsChange(editedText);
  };

  const handleClose = () => {
    if (isDirty) {
      setShowDiscardModal(true);
      return;
    }
    onClose();
  };

  const hasTunes = tunes && tunes.length > 1 && currentIndex !== -1;

  // Find next/prev tune that has relevant content
  const findNext = (dir) => {
    if (!hasTunes) return -1;
    const field = type === 'notes' ? 'notes' : type === 'lyrics' ? 'lyrics' : 'abc';
    let i = currentIndex + dir;
    while (i >= 0 && i < tunes.length) {
      if (tunes[i][field]) return i;
      i += dir;
    }
    return -1;
  };

  const prevIndex = findNext(-1);
  const nextIndex = findNext(1);
  const headerMeta = (type === 'lyrics' || type === 'abc')
    ? [tune?.key, tune?.quickNote].filter(Boolean).join(' • ')
    : '';
  const hasContextHeader = Boolean(contextLabel && contextName && hasTunes);
  const contextSummary = hasContextHeader ? `${contextLabel}: ${contextName} • ${currentIndex + 1} of ${tunes.length}` : '';

  const handleSwipe = (direction) => {
    if (direction === 'left' && nextIndex !== -1) onNavigate(nextIndex);
    if (direction === 'right' && prevIndex !== -1) onNavigate(prevIndex);
  };

  // Trackpad two-finger horizontal swipe
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY) * 1.5) return;
      wheelAccum.current += e.deltaX;
      clearTimeout(wheelTimer.current);
      wheelTimer.current = setTimeout(() => {
        wheelAccum.current = 0;
        wheelLocked.current = false;
      }, 300);
      if (!wheelLocked.current && Math.abs(wheelAccum.current) > 80) {
        wheelLocked.current = true;
        handleSwipe(wheelAccum.current > 0 ? 'left' : 'right');
        wheelAccum.current = 0;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: true });
    return () => el.removeEventListener('wheel', onWheel);
  });

  // For ABC, require content; for text editors, allow empty.
  if (!isTextType && !tune.abc) return null;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'color-mix(in srgb, var(--bg-card, #fff) 94%, #000 6%)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        color: 'var(--text-primary, #111)',
      }}
      onTouchStart={e => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
      }}
      onTouchEnd={e => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        const dy = e.changedTouches[0].clientY - touchStartY.current;
        touchStartX.current = null;
        touchStartY.current = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          handleSwipe(dx < 0 ? 'left' : 'right');
        }
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        gap: '8px',
        flexShrink: 0,
        borderBottom: '1px solid color-mix(in srgb, var(--card-border, #ccc) 78%, transparent)',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {contextSummary && (
            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted, #666)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contextSummary}
            </div>
          )}
          <div style={{ fontWeight: 'bold', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {tune.title}
          </div>
          {headerMeta && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #666)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {headerMeta}
            </div>
          )}
        </div>
        <button
          onClick={() => isTextType ? setFontSize(s => Math.max(0.8, s - 0.2)) : setAbcScale(s => Math.max(0.4, s - 0.15))}
          style={controlBtnStyle}
        >
          A-
        </button>
        <button
          onClick={() => isTextType ? setFontSize(s => Math.min(2.5, s + 0.2)) : setAbcScale(s => Math.min(2.0, s + 0.15))}
          style={controlBtnStyle}
        >
          A+
        </button>
        {isTextType && ((type === 'notes' && onNotesChange) || (type === 'lyrics' && onLyricsChange)) && (
          <button
            onClick={handleSaveText}
            disabled={!isDirty}
            style={{
              ...controlBtnStyle,
              opacity: isDirty ? 1 : 0.4,
              backgroundColor: isDirty
                ? 'color-mix(in srgb, var(--text-link, #228be6) 68%, transparent)'
                : 'color-mix(in srgb, var(--button-bg, #eee) 72%, transparent)',
            }}
          >
            Save
          </button>
        )}
        <button onClick={handleClose} style={controlBtnStyle}>
          ✕
        </button>
      </div>

      {/* Content */}
      <div
        className={`fullscreen-viewer-content fullscreen-viewer-content-${type}`}
        style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: isTextType ? 'hidden' : 'auto',
        padding: '16px',
        ...(isTextType ? {} : { display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }),
        WebkitOverflowScrolling: 'touch',
        ...(isTextType ? { '--lyrics-font-size': `${fontSize}rem` } : {}),
      }}>
        {isTextType ? (
          <textarea
            className="fullscreen-lyrics-editor"
            ref={textareaRef}
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            onKeyDown={(e) => {
              // Tab inserts 5 spaces instead of moving focus
              if (e.key === 'Tab') {
                e.preventDefault();
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                const val = ta.value;
                const updated = val.slice(0, start) + '     ' + val.slice(end);
                setEditedText(updated);
                requestAnimationFrame(() => {
                  ta.selectionStart = ta.selectionEnd = start + 5;
                });
              }
            }}
            onBeforeInput={(e) => {
              // Intercept OS double-space → period shortcut
              if (e.data === '. ') {
                e.preventDefault();
                const ta = textareaRef.current;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                const val = ta.value;
                const updated = val.slice(0, start) + '  ' + val.slice(end);
                setEditedText(updated);
                requestAnimationFrame(() => {
                  ta.selectionStart = ta.selectionEnd = start + 2;
                });
              }
            }}
            placeholder={type === 'notes' ? 'Add notes...' : 'Add lyrics/chords...'}
            style={{
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              minHeight: '300px',
              fontFamily: "'Courier New', Courier, monospace",
              lineHeight: 1.5,
              color: 'var(--text-primary, #111)',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <div style={{
            width: 'fit-content',
            transform: `scale(${abcScale})`,
            transformOrigin: 'top center',
          }}>
            <AbcNotation abc={tune.abc} />
          </div>
        )}
      </div>

      {/* Footer navigation */}
      {hasTunes && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 16px',
          flexShrink: 0,
          borderTop: '1px solid color-mix(in srgb, var(--card-border, #ccc) 78%, transparent)',
        }}>
          {prevIndex !== -1 ? (
            <button onClick={() => onNavigate(prevIndex)} style={navBtnStyle}>
              ← Prev
            </button>
          ) : <div style={{ width: '80px' }} />}

          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted, #666)' }}>
            {currentIndex + 1} of {tunes.length}
          </span>

          {nextIndex !== -1 ? (
            <button onClick={() => onNavigate(nextIndex)} style={navBtnStyle}>
              Next →
            </button>
          ) : <div style={{ width: '80px' }} />}
        </div>
      )}

      <ActionConfirmModal
        open={showDiscardModal}
        title="Unsaved Changes"
        message={`You have unsaved ${type === 'notes' ? 'notes' : 'lyrics/chords'} changes. Leave and discard them?`}
        confirmLabel="Leave"
        cancelLabel="Stay"
        danger
        onConfirm={() => {
          setShowDiscardModal(false);
          onClose();
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
    </div>
  );
}

const controlBtnStyle = {
  background: 'color-mix(in srgb, var(--button-bg, #eee) 72%, transparent)',
  color: 'var(--text-primary, #111)',
  border: '1px solid color-mix(in srgb, var(--card-border, #ccc) 72%, transparent)',
  borderRadius: '6px',
  padding: '6px 12px',
  fontSize: '0.9rem',
  cursor: 'pointer',
  fontWeight: 'bold',
};

const navBtnStyle = {
  background: 'color-mix(in srgb, var(--button-bg, #eee) 72%, transparent)',
  color: 'var(--text-primary, #111)',
  border: '1px solid color-mix(in srgb, var(--card-border, #ccc) 72%, transparent)',
  borderRadius: '8px',
  padding: '8px 16px',
  fontSize: '0.9rem',
  cursor: 'pointer',
};
